/**
 * SearchInput — Reusable search box that only triggers filtering
 * after the user has typed at least `minChars` characters (default 3).
 *
 * Usage:
 *   <SearchInput
 *     placeholder="Search organizations..."
 *     onSearch={(term) => setSearchQuery(term)}
 *   />
 */

import * as React from 'react'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface SearchInputProps {
  /** Called with the active search term (empty string = cleared / below min chars) */
  onSearch: (value: string) => void
  placeholder?: string
  /** Minimum characters before filtering begins (default: 3) */
  minChars?: number
  className?: string
}

export function SearchInput({
  onSearch,
  placeholder = 'Search...',
  minChars = 3,
  className,
}: SearchInputProps) {
  const [inputValue, setInputValue] = React.useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)

    // Trigger search only when cleared or at/above min char threshold
    if (val.length === 0 || val.length >= minChars) {
      onSearch(val)
    } else {
      // Below threshold — clear any active filter so full list shows
      onSearch('')
    }
  }

  const handleClear = () => {
    setInputValue('')
    onSearch('')
  }

  const remaining = minChars - inputValue.length
  const showHint = inputValue.length > 0 && inputValue.length < minChars

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          className="pl-9 pr-9"
        />
        {inputValue.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {showHint && (
        <p className="text-xs text-muted-foreground pl-1">
          Type {remaining} more character{remaining !== 1 ? 's' : ''} to search
        </p>
      )}
    </div>
  )
}
